import { MutableRefObject, useEffect, useRef, useState } from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import { ReactState } from '../Contexts'
import { incrementCounterRef } from '../Utils/NumberUtils'

export type LoaderPromiseAppender = (...promise: Promise<unknown>[]) => void

export type MultiRequestLoading = {
    isLoading?: boolean
    appendLoaderPromise: LoaderPromiseAppender
}

export type MultiRequestLoadingProps = {
    loaderState?: ReactState<boolean>
    callback?: (results?: unknown[]) => unknown
}

/**
 * When you have multiple requests that contribute to the loading of a single content area, you can use
 * this function to create a loading state for them.
 *
 * @param loaderState - Optional react state for the loading state.
 * @param callback - A function that is called after all of the promises are completed.
 *
 * @returns An object containing the isLoading variable and a function to append new promises to listen to.
 */
export const useMultiRequestLoading = ({ loaderState, callback }: MultiRequestLoadingProps): MultiRequestLoading => {
    const [loaderPromises, setLoaderPromises]: ReactState<Promise<unknown>[]> = useState<Promise<unknown>[]>([])
    const [isLoading, setIsLoading]: ReactState<boolean> = loaderState ?? useState<boolean>(false)

    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const loaded: MutableRefObject<number> = useRef(0)

    /**
     * Loaded promise function runs after the promises complete.
     *
     * @param results - The data returned from the promises.
     */
    const loadedPromiseFunction = (results?: unknown[]): void => {
        // Increment the count of loaded.
        incrementCounterRef(loaded)

        // Are all of the promises completed.
        if (loaded.current === loaderPromises.length && isMounted.current) {
            setTimeout((): void => {
                // Set the loading state to not loading.
                setIsLoading(false)

                if (callback) {
                    // Fire the callback function.
                    callback(results)
                }
            })
        }
    }

    useEffect(() => {
        if (loaderPromises.length > 0) {
            setIsLoading(true)
            // Wait for all promises to complete before firing the callback.
            Promise.all(loaderPromises).then(loadedPromiseFunction).catch(loadedPromiseFunction)
        }
    }, [JSON.stringify(loaderPromises)])

    return {
        isLoading,
        // Add new promises to the list.
        appendLoaderPromise: (...promise: Promise<unknown>[]): void =>
            setLoaderPromises([...loaderPromises, ...promise])
    }
}
